import { useLayoutEffect, useRef, useState } from 'react';
import type { Viaje } from '../lib/appTypes';
import { Modal } from './ui-kit';

type Columna = { clave: string; titulo: string; alineacion?: 'right' | 'center'; moneda?: boolean };
type GrupoProductos = {
  titulo: string;
  subtitulo?: string;
  columnas: Columna[];
  filas: Array<Record<string, string | number | null | undefined>>;
  total?: number | null;
};

function formatoMoneda(valor?: number | null): string {
  if (typeof valor !== 'number' || Number.isNaN(valor)) return '—';
  return new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(valor);
}

function formatoCantidad(valor?: number | null): string {
  if (typeof valor !== 'number' || Number.isNaN(valor)) return '—';
  return Number.isInteger(valor) ? String(valor) : valor.toFixed(2);
}

/** Separa la descripción libre ("Factura: X\nproducto A, producto B") en grupos de productos. */
function productosDesdeTexto(descripcion: string): GrupoProductos[] {
  const secciones = descripcion.includes('Factura:')
    ? descripcion.split(/(?=Factura:)/).map(seccion => seccion.trim()).filter(Boolean)
    : [descripcion.trim()];

  return secciones.map((seccion, indice) => {
    const [primeraLinea = '', ...resto] = seccion.split('\n');
    const tieneFactura = primeraLinea.startsWith('Factura:');
    const cuerpo = (tieneFactura ? resto.join('\n') : seccion).replace(/\n+/g, ', ');
    const filas = cuerpo
      .split(/,\s+(?=\S)/)
      .map(texto => texto.trim())
      .filter(Boolean)
      .map((texto, posicion) => ({ n: posicion + 1, descripcion: texto }));
    return {
      titulo: tieneFactura ? primeraLinea.trim() : secciones.length > 1 ? `Grupo ${indice + 1}` : 'Productos',
      columnas: [
        { clave: 'n', titulo: '#', alineacion: 'center' as const },
        { clave: 'descripcion', titulo: 'Producto' },
      ],
      filas,
    };
  });
}

/** Productos del viaje: ítems de las facturas, ítems de la transferencia o, si no hay, el texto de la descripción. */
export function productosDeViaje(viaje: Viaje): GrupoProductos[] {
  const grupos: GrupoProductos[] = [];

  (viaje.paquete?.guiasPdf ?? []).forEach((guia, indice) => {
    const items = guia.extraccion?.items ?? [];
    if (items.length === 0) return;
    grupos.push({
      titulo: guia.numeroFactura ? `Factura ${guia.numeroFactura}` : `Guía ${indice + 1}`,
      subtitulo: guia.direccion,
      columnas: [
        { clave: 'codigo', titulo: 'Código' },
        { clave: 'descripcion', titulo: 'Descripción' },
        { clave: 'cantidad', titulo: 'Cant.', alineacion: 'center' },
        { clave: 'precioUnitario', titulo: 'P. unit.', alineacion: 'right', moneda: true },
        { clave: 'precioTotal', titulo: 'Total', alineacion: 'right', moneda: true },
      ],
      filas: items.map(item => ({
        codigo: item.codigo || '—',
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        precioUnitario: item.precioUnitario,
        precioTotal: item.precioTotal,
      })),
      total: guia.valorTotal ?? guia.extraccion?.total ?? null,
    });
  });

  const itemsTransferencia = viaje.transferencia?.items ?? [];
  if (itemsTransferencia.length > 0) {
    grupos.push({
      titulo: viaje.transferencia?.numero ? `Transferencia ${viaje.transferencia.numero}` : 'Transferencia',
      subtitulo: [viaje.transferencia?.bodegaOrigen, viaje.transferencia?.bodegaDestino].filter(Boolean).join(' → ') || undefined,
      columnas: [
        { clave: 'codigo', titulo: 'Código' },
        { clave: 'producto', titulo: 'Producto' },
        { clave: 'marca', titulo: 'Marca' },
        { clave: 'cantidad', titulo: 'Cant.', alineacion: 'center' },
      ],
      filas: itemsTransferencia.map(item => ({
        codigo: item.codigoComercial || item.codigo || '—',
        producto: item.producto,
        marca: item.marcaRepuesto || '—',
        cantidad: item.cantidad,
      })),
    });
  }

  if (grupos.length === 0 && viaje.paquete?.descripcion?.trim()) {
    return productosDesdeTexto(viaje.paquete.descripcion);
  }
  return grupos;
}

/** Popup con el detalle completo de la entrega: datos generales y productos en tabla. */
export function DetalleProductosModal({ viaje, onClose }: { viaje: Viaje; onClose: () => void }) {
  const grupos = productosDeViaje(viaje);
  const totalFilas = grupos.reduce((suma, grupo) => suma + grupo.filas.length, 0);
  const descripcion = viaje.paquete?.descripcion?.trim();

  return (
    <Modal onClose={onClose} maxWidthClassName="max-w-4xl" ariaLabel="Detalle de la entrega">
      <div className="border-b border-stone-200 px-6 py-5 pr-16">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-500">Detalle de la entrega</p>
        <h3 className="mt-1 text-xl font-semibold text-stone-950">{viaje.paquete?.nombre || 'Entrega sin nombre'}</h3>
        <p className="mt-1 text-sm text-stone-500">
          {[viaje.paquete?.cliente?.nombre, `${totalFilas} ${totalFilas === 1 ? 'producto' : 'productos'}`].filter(Boolean).join(' · ')}
        </p>
      </div>

      <div className="space-y-5 px-6 py-5">
        {grupos.length === 0 ? (
          <p className="rounded-[var(--radius-panel)] border border-dashed border-stone-300 p-6 text-center text-sm text-stone-500">
            Esta entrega no tiene productos registrados.
          </p>
        ) : (
          grupos.map((grupo, indice) => (
            <section key={`${grupo.titulo}-${indice}`} className="overflow-hidden rounded-[var(--radius-panel)] border border-stone-200">
              <div className="flex flex-wrap items-baseline justify-between gap-2 bg-stone-50 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-stone-900">{grupo.titulo}</p>
                  {grupo.subtitulo && <p className="text-xs text-stone-500">{grupo.subtitulo}</p>}
                </div>
                <p className="text-xs font-semibold text-stone-500">
                  {grupo.filas.length} {grupo.filas.length === 1 ? 'línea' : 'líneas'}
                  {typeof grupo.total === 'number' && <> · Total {formatoMoneda(grupo.total)}</>}
                </p>
              </div>
              {/* Celular: cada producto como ficha compacta. */}
              <ul className="divide-y divide-stone-100 sm:hidden">
                {grupo.filas.map((fila, posicion) => {
                  const nombre = String(fila.descripcion ?? fila.producto ?? '—');
                  const detalles = [
                    fila.codigo && fila.codigo !== '—' ? String(fila.codigo) : '',
                    typeof fila.cantidad === 'number' ? `Cant. ${formatoCantidad(fila.cantidad)}` : '',
                    fila.marca && fila.marca !== '—' ? String(fila.marca) : '',
                    typeof fila.precioUnitario === 'number' ? `P. unit. ${formatoMoneda(fila.precioUnitario)}` : '',
                  ].filter(Boolean);
                  return (
                    <li key={posicion} className="flex items-start justify-between gap-3 px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-stone-900">
                          {typeof fila.n === 'number' && <span className="mr-1.5 text-stone-400">{fila.n}.</span>}
                          {nombre}
                        </p>
                        {detalles.length > 0 && <p className="mt-0.5 text-xs text-stone-500">{detalles.join(' · ')}</p>}
                      </div>
                      {typeof fila.precioTotal === 'number' && (
                        <span className="shrink-0 text-sm font-semibold text-stone-900">{formatoMoneda(fila.precioTotal)}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="hidden overflow-x-auto sm:block">
                <table className="ui-tabla min-w-full text-left text-sm text-stone-700">
                  <thead className="text-[11px] uppercase tracking-[0.14em] text-stone-500">
                    <tr className="border-b border-stone-200">
                      {grupo.columnas.map(columna => (
                        <th
                          key={columna.clave}
                          className={`whitespace-nowrap px-4 py-2.5 font-semibold ${columna.alineacion === 'right' ? 'text-right' : columna.alineacion === 'center' ? 'text-center' : ''}`}
                        >
                          {columna.titulo}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.filas.map((fila, posicion) => (
                      <tr key={posicion} className="border-t border-stone-100 align-top">
                        {grupo.columnas.map(columna => {
                          const valor = fila[columna.clave];
                          const texto = columna.moneda
                            ? formatoMoneda(typeof valor === 'number' ? valor : null)
                            : columna.clave === 'cantidad'
                              ? formatoCantidad(typeof valor === 'number' ? valor : null)
                              : String(valor ?? '—');
                          return (
                            <td
                              key={columna.clave}
                              className={`px-4 py-2.5 ${columna.alineacion === 'right' ? 'whitespace-nowrap text-right' : columna.alineacion === 'center' ? 'text-center' : ''} ${
                                columna.clave === 'codigo' ? 'whitespace-nowrap font-mono text-xs text-stone-500' : ''
                              } ${columna.clave === 'descripcion' || columna.clave === 'producto' ? 'font-medium text-stone-900' : ''}`}
                            >
                              {texto}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}

        {descripcion && (
          <details className="group rounded-[var(--radius-panel)] border border-stone-200 px-4 py-3 text-sm">
            <summary className="cursor-pointer select-none font-semibold text-stone-700">
              <i className="fa-solid fa-chevron-right mr-2 text-xs transition-transform group-open:rotate-90" aria-hidden="true" />
              Ver descripción original
            </summary>
            <p className="mt-3 whitespace-pre-line text-stone-600">{descripcion}</p>
          </details>
        )}
      </div>
    </Modal>
  );
}

/**
 * Descripción limitada a pocas líneas con "…" y botón "Ver detalles" que abre el popup con la tabla
 * de productos. El botón aparece cuando el texto se corta o cuando hay productos que mostrar.
 */
export function DescripcionResumida({
  viaje,
  texto,
  lineas = 3,
  className = '',
}: {
  viaje: Viaje;
  texto: string;
  lineas?: 2 | 3 | 4;
  className?: string;
}) {
  const parrafoRef = useRef<HTMLParagraphElement>(null);
  const [cortado, setCortado] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const claseLineas = lineas === 2 ? 'line-clamp-2' : lineas === 4 ? 'line-clamp-4' : 'line-clamp-3';

  useLayoutEffect(() => {
    const medir = () => {
      const parrafo = parrafoRef.current;
      if (parrafo) setCortado(parrafo.scrollHeight - parrafo.clientHeight > 2);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [texto]);

  const hayProductos = productosDeViaje(viaje).some(grupo => grupo.filas.length > 1);

  return (
    <div className={className}>
      <p ref={parrafoRef} className={`${claseLineas} whitespace-pre-line break-words`} title={cortado ? texto : undefined}>
        {texto}
      </p>
      {(cortado || hayProductos) && (
        <button
          type="button"
          onClick={() => setAbierto(true)}
          className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--freisa-red)] transition hover:underline"
        >
          <i className="fa-solid fa-list-ul" aria-hidden="true" />
          Ver detalles
        </button>
      )}
      {abierto && <DetalleProductosModal viaje={viaje} onClose={() => setAbierto(false)} />}
    </div>
  );
}
